import { useEffect, useRef } from 'react'
import type { ReactNode } from 'react'
import { createPortal } from 'react-dom'

interface ModalProps {
    onClose: () => void;
    ariaLabel: string;
    children: ReactNode;
}

const FOCUSABLE_SELECTOR =
    'a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])'

// Generic accessible modal. Callers control mount/unmount (render this only
// while open) rather than passing an isOpen flag — same precedent as the old
// site's ContactForm (`{showContactForm && <ContactForm .../>}`). Mounting
// is opening; unmounting runs the cleanup below, which is what restores
// scroll and focus.
//
// onClose is read via a ref rather than the prop directly so the mount
// effect (which does the one-time focus-move, scroll-lock, and listener
// setup) doesn't need onClose in its dependency array — callers pass a new
// inline closure on every render, and re-running that setup on every parent
// re-render would steal focus back into the modal repeatedly.
export function Modal({ onClose, ariaLabel, children }: ModalProps) {
    const panelRef = useRef<HTMLDivElement>(null)
    const onCloseRef = useRef(onClose)

    useEffect(() => {
        onCloseRef.current = onClose
    })

    useEffect(() => {
        const previouslyFocused = document.activeElement as HTMLElement | null
        const panel = panelRef.current

        // Focus the panel itself, not an arbitrary first descendant — this
        // content has no natural "first field" the way a form dialog would,
        // and focusing the dialog announces its role/label immediately.
        panel?.focus()

        const originalOverflow = document.body.style.overflow
        document.body.style.overflow = 'hidden'

        function handleKeyDown(event: KeyboardEvent) {
            if (event.key === 'Escape') {
                event.preventDefault()
                onCloseRef.current()
                return
            }

            if (event.key !== 'Tab' || !panel) return

            const currentFocusables = Array.from(panel.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR))
            if (currentFocusables.length === 0) return

            const first = currentFocusables[0]
            const last = currentFocusables[currentFocusables.length - 1]

            if (event.shiftKey && document.activeElement === first) {
                event.preventDefault()
                last.focus()
            } else if (!event.shiftKey && document.activeElement === last) {
                event.preventDefault()
                first.focus()
            }
        }

        document.addEventListener('keydown', handleKeyDown)

        return () => {
            document.removeEventListener('keydown', handleKeyDown)
            document.body.style.overflow = originalOverflow
            previouslyFocused?.focus()
        }
    }, [])

    return createPortal(
        <div
            data-testid="modal-overlay"
            className="fixed inset-0 z-50 overflow-y-auto bg-unimo-slate/55"
            onClick={() => onCloseRef.current()}
        >
            <div className="flex min-h-full items-center justify-center p-4 lg:p-8">
                <div
                    ref={panelRef}
                    role="dialog"
                    aria-modal="true"
                    aria-label={ariaLabel}
                    tabIndex={-1}
                    onClick={(event) => event.stopPropagation()}
                    className="relative w-full max-w-[720px] rounded-site-panel bg-unimo-stone p-8 lg:p-10"
                >
                    <button
                        type="button"
                        onClick={() => onCloseRef.current()}
                        aria-label="Close"
                        className="absolute right-4 top-4 text-unimo-slate"
                    >
                        <svg aria-hidden="true" className="h-6 w-6" viewBox="0 0 24 24" fill="none">
                            <path
                                d="M6 6L18 18M18 6L6 18"
                                stroke="currentColor"
                                strokeWidth="1.5"
                                strokeLinecap="round"
                            />
                        </svg>
                    </button>
                    {children}
                </div>
            </div>
        </div>,
        document.body
    )
}

export default Modal
